import { useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useAdminProperty } from "@/hooks/use-admin-property";
import { selectedPropertyParam, withPropertyParam } from "@/lib/admin-scope";
import { cn } from "@/lib/utils";
import { ADMIN_STYLES } from "@/lib/admin-styles";
import {
  Home,
  Calendar,
  BookOpen,
  Settings,
  LogOut,
  Edit,
  Star,
  Inbox,
  Menu,
  Newspaper,
  BarChart3,
  ShieldCheck,
  Compass,
  Send,
  Users,
  Building2,
  Scale,
  ChevronRight,
  MailCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { useSiteBrand } from "@/hooks/use-site-brand";

interface NavBadgeCounts {
  pendingBookings?: number;
  pendingReviews?: number;
  unreadCommunications?: number;
}

function NavBadge({ count, label }: { count: number; label: string }) {
  if (count <= 0) return null;
  const display = count > 99 ? "99+" : String(count);
  return (
    <span
      className="ml-auto inline-flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-sun px-2 font-sans text-[13px] font-semibold tabular-nums leading-none text-white"
      aria-label={`${display} ${label}`}
      data-testid="nav-badge"
    >
      {display}
    </span>
  );
}

function SidebarNav({ onNavigate, badges }: { onNavigate?: () => void; badges: NavBadgeCounts }) {
  const [location] = useLocation();
  const { logoutMutation } = useAuth();

  const navigation = [
    {
      name: "Dashboard",
      section: "Daily operations",
      href: "/admin",
      icon: Home,
      current: location === "/admin",
      badge: 0,
    },
    {
      name: "Bookings",
      section: "Daily operations",
      href: "/admin/bookings",
      icon: BookOpen,
      current: location === "/admin/bookings" || location.startsWith("/admin/bookings"),
      badge: badges.pendingBookings ?? 0,
    },
    {
      name: "Calendar",
      section: "Daily operations",
      href: "/admin/calendar",
      icon: Calendar,
      current: location === "/admin/calendar",
      badge: 0,
    },
    {
      name: "Inbox",
      section: "Daily operations",
      href: "/admin/communications",
      icon: Inbox,
      current:
        location === "/admin/communications" ||
        location.startsWith("/admin/communications") ||
        location.startsWith("/admin/chat"),
      badge: badges.unreadCommunications ?? 0,
    },
    {
      name: "Emails",
      section: "Daily operations",
      href: "/admin/emails",
      icon: MailCheck,
      current: location.startsWith("/admin/emails"),
      badge: 0,
    },
    {
      name: "Reviews",
      section: "Daily operations",
      href: "/admin/reviews",
      icon: Star,
      current: location === "/admin/reviews",
      badge: badges.pendingReviews ?? 0,
    },
    {
      name: "Properties",
      section: "Website content",
      href: "/admin/properties",
      icon: Building2,
      current: location === "/admin/properties" || location.startsWith("/admin/properties"),
      badge: 0,
    },
    {
      name: "Content Management",
      section: "Website content",
      href: "/admin/content-management",
      icon: Edit,
      current: location === "/admin/content-management",
      badge: 0,
    },
    {
      name: "Legal Documents",
      section: "Website content",
      href: "/admin/legal",
      icon: Scale,
      current: location === "/admin/legal",
      badge: 0,
    },
    {
      name: "Blog",
      section: "Website content",
      href: "/admin/blog",
      icon: Newspaper,
      current: location === "/admin/blog",
      badge: 0,
    },
    {
      name: "Reporting",
      section: "Growth & SEO",
      href: "/admin/reporting",
      icon: BarChart3,
      current: location === "/admin/reporting",
      badge: 0,
    },
    {
      name: "Backlink Health",
      section: "Growth & SEO",
      href: "/admin/backlinks",
      icon: ShieldCheck,
      current: location === "/admin/backlinks",
      badge: 0,
    },
    {
      name: "Link Opportunities",
      section: "Growth & SEO",
      href: "/admin/link-opportunities",
      icon: Compass,
      current: location === "/admin/link-opportunities",
      badge: 0,
    },
    {
      name: "Outreach",
      section: "Growth & SEO",
      href: "/admin/outreach",
      icon: Send,
      current: location === "/admin/outreach",
      badge: 0,
    },
    {
      name: "Settings",
      section: "Setup",
      href: "/admin/settings",
      icon: Settings,
      current: location === "/admin/settings" || location === "/admin/pricing",
      badge: 0,
    },
    {
      name: "Team",
      section: "Setup",
      href: "/admin/users",
      icon: Users,
      current: location === "/admin/users",
      badge: 0,
    },
  ];

  // Carry the selected property across navigation.
  //
  // The switcher stores the choice in `?property=`, and every link here was a bare
  // path, so the selection was silently dropped the moment you clicked anything and
  // the next screen quietly reverted to all properties. With one home that was
  // invisible; with two or more it means believing you are looking at one home's
  // bookings while reading another's. The param rides along even onto the
  // site-wide screens, which ignore it, so a detour through Blog does not lose
  // the choice you made before it.
  // Preserves declaration order: each section appears where its first item does.
  const sections = navigation.reduce<{ section: string; items: typeof navigation }[]>(
    (acc, item) => {
      const last = acc[acc.length - 1];
      if (last && last.section === item.section) last.items.push(item);
      else acc.push({ section: item.section, items: [item] });
      return acc;
    },
    [],
  );

  // The daily pages (bookings, calendar, inbox, reviews) stay in plain view;
  // the other groups start closed, per the owner's request, so the rarely used
  // growth and setup screens do not crowd the ones used every day. The header
  // of the section holding the current page keeps full-strength ink, so a shut
  // sidebar still hints at where you are.
  const ALWAYS_OPEN = "Daily operations";
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const sectionId = (section: string) => `nav-section-${section.replace(/[^a-z]+/gi, "-").toLowerCase()}`;
  const isOpen = (section: string) => section === ALWAYS_OPEN || (openSections[section] ?? false);
  const toggle = (section: string) =>
    setOpenSections((prev) => ({ ...prev, [section]: !(prev[section] ?? false) }));
  const hasCurrent = (items: typeof navigation) => items.some((i) => i.current);

  const propertyParam = selectedPropertyParam(useSearch());
  const withProperty = (href: string) => withPropertyParam(href, propertyParam);

  // Name the home being worked on, not a fixed brand. Reading "The Kualoa Beach
  // House" in the corner while editing a different property is the kind of small
  // wrongness that makes someone doubt every other label on the screen.
  const { active, properties } = useAdminProperty();
  const siteBrand = useSiteBrand();
  const shellTitle = active?.title ?? (properties.length > 1 ? "All properties" : siteBrand.name);

  return (
    <>
      <div className="flex flex-col px-4 py-3 border-b border-border">
        <span
          className="font-serif font-bold text-lg text-foreground truncate"
          title={shellTitle}
          data-testid="admin-shell-title"
        >
          {shellTitle}
        </span>
        {active && !active.show_on_website && (
          <span className="font-sans text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            Hidden from website
          </span>
        )}
      </div>

      {/*
        Sixteen links in one undifferentiated column. Nothing was in the wrong
        place — the order already grouped itself — so this labels the groups that
        were already there rather than moving anything. Muscle memory survives;
        the list stops reading as a single sixteen-item scan.
      */}
      <nav className="flex-1 px-3 py-4" aria-label="Admin sections">
        {sections.map(({ section, items }) => (
          <div key={section} className="mb-4 last:mb-0">
            {/*
              A disclosure button, not an <h2>. The list it controls is named by
              it either way, so a screen reader still announces "Growth & SEO,
              list, 4 items" - but four sidebar headings ahead of every page's h1
              would clutter the heading outline that heading navigation depends on.
            */}
            <button
              type="button"
              id={sectionId(section)}
              aria-expanded={isOpen(section)}
              aria-controls={`${sectionId(section)}-items`}
              onClick={() => section !== ALWAYS_OPEN && toggle(section)}
              disabled={section === ALWAYS_OPEN}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left transition-colors max-lg:min-h-11",
                section !== ALWAYS_OPEN && "hover:bg-muted",
                ADMIN_STYLES.typography.menuSectionHeader,
                hasCurrent(items) ? "text-ink" : "text-ink/80",
              )}
              data-testid={sectionId(section)}
            >
              {section !== ALWAYS_OPEN && (
                <ChevronRight
                  className={cn(
                    "h-3.5 w-3.5 shrink-0 transition-transform",
                    isOpen(section) && "rotate-90",
                  )}
                  aria-hidden="true"
                />
              )}
              <span className="min-w-0 flex-1 truncate">{section}</span>
              {/*
                A closed section would otherwise swallow its counts entirely, and
                43 unread messages should not disappear because Daily operations
                happens to be shut.
              */}
              {!isOpen(section) && (
                <NavBadge
                  count={items.reduce((n, i) => n + (i.badge || 0), 0)}
                  label={`items needing attention in ${section}`}
                />
              )}
            </button>
            <ul
              id={`${sectionId(section)}-items`}
              className="space-y-0.5 pl-3"
              aria-labelledby={sectionId(section)}
              hidden={!isOpen(section)}
            >
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <li key={item.name}>
            <Link
              href={withProperty(item.href)}
              onClick={onNavigate}
              className={cn(
                "flex items-center px-3 py-2 rounded-md transition-colors max-lg:min-h-11 max-lg:text-[15px]",
                item.current
                  ? `${ADMIN_STYLES.typography.menuItemActive} bg-sand text-ink`
                  : `${ADMIN_STYLES.typography.menuItem} text-muted-foreground hover:text-foreground hover:bg-muted`,
              )}
              data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon className="h-4 w-4 mr-2.5 flex-shrink-0" />
              <span className="min-w-0 flex-1 truncate">{item.name}</span>
              <NavBadge
                count={item.badge}
                label={item.name === "Bookings" ? "paid bookings awaiting approval" : item.name === "Reviews" ? "reviews awaiting approval" : "guest messages waiting for a reply"}
              />
            </Link>
            </li>
          );
        })}
            </ul>
          </div>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-border">
        <Link href="/">
          <Button
            variant="bronze"
            size="sm"
            className={`${ADMIN_STYLES.typography.buttonSmall} w-full mb-1.5 h-8`}
            onClick={onNavigate}
          >
            View Site
          </Button>
        </Link>
        <Button
          variant="ghost"
          size="sm"
          className={`${ADMIN_STYLES.typography.buttonSmall} w-full text-muted-foreground hover:text-destructive h-8`}
          onClick={() => logoutMutation.mutate()}
          disabled={logoutMutation.isPending}
          data-testid="admin-logout"
        >
          <LogOut className="h-3.5 w-3.5 mr-2" />
          Logout
        </Button>
      </div>
    </>
  );
}

/**
 * Phone navigation for the four screens used every day. On a phone the only
 * way between admin pages was the hamburger menu, so checking the inbox after
 * the calendar was three taps. A fixed bar keeps them one tap away; everything
 * else lives behind "More", which opens the full menu.
 */
function AdminBottomBar({ badges, onMore }: { badges: NavBadgeCounts; onMore: () => void }) {
  const [location] = useLocation();
  const propertyParam = selectedPropertyParam(useSearch());
  const items = [
    { name: "Home", href: "/admin", icon: Home, current: location === "/admin", badge: 0 },
    { name: "Bookings", href: "/admin/bookings", icon: BookOpen, current: location.startsWith("/admin/bookings"), badge: badges.pendingBookings ?? 0 },
    { name: "Calendar", href: "/admin/calendar", icon: Calendar, current: location === "/admin/calendar", badge: 0 },
    { name: "Inbox", href: "/admin/communications", icon: Inbox, current: location.startsWith("/admin/communications") || location.startsWith("/admin/chat"), badge: badges.unreadCommunications ?? 0 },
  ];
  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 grid grid-cols-5 border-t border-border bg-card pb-[env(safe-area-inset-bottom)]"
      aria-label="Quick navigation"
      data-testid="admin-bottom-bar"
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <Link
            key={item.name}
            href={withPropertyParam(item.href, propertyParam)}
            className={cn(
              "relative flex h-14 flex-col items-center justify-center gap-0.5 font-sans text-[11.5px] font-medium",
              item.current ? "text-ink after:absolute after:inset-x-4 after:top-0 after:h-0.5 after:rounded-full after:bg-ink" : "text-muted-foreground",
            )}
            aria-current={item.current ? "page" : undefined}
            data-testid={`bottom-nav-${item.name.toLowerCase()}`}
          >
            <Icon className="h-5 w-5" aria-hidden="true" />
            {item.name}
            {item.badge > 0 && (
              <span className="absolute left-1/2 top-1.5 ml-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-sun px-1 text-[10px] font-semibold leading-none text-white">
                {item.badge > 99 ? "99+" : item.badge}
              </span>
            )}
          </Link>
        );
      })}
      <button
        type="button"
        onClick={onMore}
        className="flex h-14 flex-col items-center justify-center gap-0.5 font-sans text-[11.5px] font-medium text-muted-foreground"
        aria-label="Open the full admin menu"
        data-testid="bottom-nav-more"
      >
        <Menu className="h-5 w-5" aria-hidden="true" />
        More
      </button>
    </nav>
  );
}

export function AdminSidebar() {
  const siteBrand = useSiteBrand();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: badges } = useQuery<NavBadgeCounts>({
    queryKey: ["/api/admin/nav-badges"],
    refetchInterval: 15000,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });

  const counts: NavBadgeCounts = {
    pendingBookings: badges?.pendingBookings ?? 0,
    pendingReviews: badges?.pendingReviews ?? 0,
    unreadCommunications: badges?.unreadCommunications ?? 0,
  };

  return (
    <>
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 flex items-center h-14 px-4 bg-card border-b border-border gap-3">
        <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} aria-label="Open admin menu">
          <Menu className="h-5 w-5" />
        </Button>
        <span className="font-serif font-bold text-base text-foreground whitespace-nowrap">{siteBrand.name}</span>
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-56 p-0 flex flex-col overflow-y-auto">
          <SidebarNav onNavigate={() => setMobileOpen(false)} badges={counts} />
        </SheetContent>
      </Sheet>

      <div className="hidden lg:flex flex-col w-56 bg-card border-r border-border min-h-screen">
        <SidebarNav badges={counts} />
      </div>

      <AdminBottomBar badges={counts} onMore={() => setMobileOpen(true)} />
    </>
  );
}
